import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import moment from "moment";
import {
  Alert,
  Button,
  Card,
  Col,
  DatePicker,
  Empty,
  Icon,
  Input,
  Pagination,
  Row,
  Select,
  Spin,
  Tag,
} from "antd";
import { useSelector } from "react-redux";
import useStockItemContext from "../../../hooks/useStockItemContext";
import DanfeViewerModal from "../../../components/utils/invoiceUploadViewer/DanfeViewerModal";
import { callStockItemJournal } from "../../../services/stockItemService";
import { momentTimeZoneHour } from "../../../utils/dateUtils";
import { TYPE_TAG_COLOR, toI18nKey } from "./movements/stockMovementConstants";
import { numberMask } from "../../../services/helpersMethodsService";

const { Option } = Select;
const { RangePicker } = DatePicker;

const PAGE_SIZE = 10;

const TYPE_BORDER_COLOR = {
  ENTRY: "#52c41a",
  EXIT: "#f5222d",
};

const SORT_OPTIONS = [
  {
    value: "stockMovementDate",
    labelKey: "movementDate",
    fallback: "Data da movimentação",
  },
  { value: "createdAt", labelKey: "createdAt", fallback: "Data de registro" },
  { value: "amount", labelKey: "amount", fallback: "Quantidade" },
  { value: "unitCost", labelKey: "unitCost", fallback: "Custo unitário" },
  { value: "cost", labelKey: "cost", fallback: "Custo total" },
];

const INITIAL_FILTERS = {
  search: "",
  type: undefined,
  origin: undefined,
  dateRange: [],
  sortField: "stockMovementDate",
  sortDirection: "descend",
};

const formatNumber = (value) =>
  value != null
    ? Number(value).toLocaleString("pt-BR", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 4,
      })
    : "-";

const formatCurrency = (value) =>
  value != null ? numberMask(value, true, null, null, 5, 5) : "-";

const getSortValue = (row, field) => {
  if (field === "stockMovementDate" || field === "createdAt") {
    return row[field] ? moment(row[field]).valueOf() : 0;
  }
  return Number(row[field]) || 0;
};

const Field = ({ label, value }) => (
  <Col span={8} style={{ marginBottom: 8 }}>
    <div style={{ color: "#888", fontSize: 12 }}>{label}</div>
    <strong>{value != null && value !== "" ? value : "-"}</strong>
  </Col>
);

const JournalCard = ({ entry, tj, typeLabel, originLabel, onViewInvoice }) => {
  const tc = tj.card || {};

  return (
    <Card
      size="small"
      style={{
        marginBottom: 12,
        borderLeft: `4px solid ${TYPE_BORDER_COLOR[entry.stockMovementType] || "#d9d9d9"}`,
      }}
    >
      <Row
        type="flex"
        justify="space-between"
        align="middle"
        style={{ marginBottom: 12 }}
      >
        <Col>
          <Tag color={TYPE_TAG_COLOR[entry.stockMovementType]}>{typeLabel}</Tag>
          <strong>{originLabel}</strong>
        </Col>
        <Col>
          <strong>
            {entry.stockMovementDate
              ? moment(entry.stockMovementDate).format("DD/MM/YYYY")
              : "-"}
          </strong>
        </Col>
      </Row>
      <Row gutter={8}>
        <Field
          label={tc.amount || "Quantidade"}
          value={formatNumber(entry.amount)}
        />
        <Field
          label={tc.unitCost || "Custo Unit."}
          value={formatCurrency(entry.unitCost)}
        />
        <Field
          label={tc.cost || "Custo Total"}
          value={formatCurrency(entry.cost)}
        />
      </Row>
      <Row gutter={8}>
        <Field
          label={tc.cmpResult || "Custo Médio (CMP)"}
          value={formatCurrency(entry.cmpResult)}
        />
        <Field
          label={tc.amountBalance || "Saldo Qtd."}
          value={formatNumber(entry.amountBalance)}
        />
        <Field
          label={tc.costBalance || "Saldo Custo"}
          value={formatCurrency(entry.costBalance)}
        />
      </Row>
      <Row type="flex" justify="space-between" align="middle">
        <Col>
          {entry.invoiceNumber && (
            <span>
              <span style={{ color: "#888", fontSize: 12 }}>
                {tc.invoice || "NF"}:{" "}
              </span>
              <strong>{entry.invoiceNumber}</strong>
            </span>
          )}
          {entry.invoiceId && (
            <Button
              type="link"
              size="small"
              icon="file-text"
              onClick={() => onViewInvoice(entry.invoiceId)}
            >
              {tj.viewInvoice || "Ver NF-e"}
            </Button>
          )}
          {entry.requesterName && (
            <span>
              <span style={{ color: "#888", fontSize: 12 }}>
                {tc.requester || "Solicitante"}:{" "}
              </span>
              <strong>{entry.requesterName}</strong>
            </span>
          )}
        </Col>
        <Col>
          <span style={{ color: "#888", fontSize: 12 }}>
            {tc.createdAt || "Registrado em"}{" "}
            {entry.createdAt
              ? momentTimeZoneHour(entry.createdAt).format("DD/MM/YYYY HH:mm")
              : "-"}
          </span>
        </Col>
      </Row>
    </Card>
  );
};

const StockItemJournalTab = ({ itemId }) => {
  const { translation } = useSelector((state) => state.app);
  const tm = translation?.stockItem?.movements || {};
  const tj = translation?.stockItem?.journal || {};
  const tf = tj.filter || {};
  const ts = tj.sort || {};

  const { groupId, farmId } = useStockItemContext();

  const [journal, setJournal] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [filters, setFilters] = useState(INITIAL_FILTERS);
  const [page, setPage] = useState(1);
  const [invoiceIdToView, setInvoiceIdToView] = useState(null);

  useEffect(() => {
    if (!groupId || !farmId || !itemId) return undefined;

    let active = true;
    const signal = axios.CancelToken.source();

    const loadJournal = async () => {
      setIsLoading(true);
      setHasError(false);
      try {
        const { data } = await callStockItemJournal({
          groupId,
          farmId,
          id: itemId,
          signal,
        });
        if (!active) return;
        const results = data?.results || data;
        setJournal(Array.isArray(results) ? results : (results?.content ?? []));
      } catch (error) {
        if (active && !axios.isCancel(error)) {
          setJournal([]);
          setHasError(true);
        }
      } finally {
        if (active) setIsLoading(false);
      }
    };

    loadJournal();

    return () => {
      active = false;
      signal.cancel();
    };
  }, [groupId, farmId, itemId]);

  const changeFilters = (patch) => {
    setFilters((previous) => ({ ...previous, ...patch }));
    setPage(1);
  };

  const getTypeLabel = (type) =>
    (tm.type && tm.type[toI18nKey(type)]) || type || "-";

  const getOriginLabel = (origin, type) => {
    const key = toI18nKey(origin);
    const entryLabel = tm.entryOrigins && tm.entryOrigins[key];
    const exitLabel = tm.exitOrigins && tm.exitOrigins[key];
    let label;
    if (type === "ENTRY") label = entryLabel;
    else if (type === "EXIT") label = exitLabel;
    else label = entryLabel || exitLabel;
    return label || origin || "-";
  };

  const originOptions = useMemo(
    () =>
      Array.from(
        new Set(
          journal
            .filter(
              (entry) =>
                !filters.type || entry.stockMovementType === filters.type,
            )
            .map((entry) => entry.stockMovementOrigin)
            .filter(Boolean),
        ),
      ),
    [journal, filters.type],
  );

  const visibleEntries = useMemo(() => {
    const term = filters.search.trim().toLowerCase();
    const [startDate, endDate] = filters.dateRange;
    const direction = filters.sortDirection === "ascend" ? 1 : -1;

    return journal
      .filter((entry) => {
        if (filters.type && entry.stockMovementType !== filters.type) {
          return false;
        }
        if (filters.origin && entry.stockMovementOrigin !== filters.origin) {
          return false;
        }
        if (startDate && endDate) {
          const movementDate = moment(entry.stockMovementDate);
          if (!movementDate.isBetween(startDate, endDate, "day", "[]")) {
            return false;
          }
        }
        if (term) {
          return (
            (entry.invoiceNumber || "").toLowerCase().includes(term) ||
            (entry.requesterName || "").toLowerCase().includes(term)
          );
        }
        return true;
      })
      .sort(
        (a, b) =>
          (getSortValue(a, filters.sortField) -
            getSortValue(b, filters.sortField)) *
            direction ||
          (getSortValue(a, "createdAt") - getSortValue(b, "createdAt")) *
            direction,
      );
  }, [journal, filters]);

  const pagedEntries = visibleEntries.slice(
    (page - 1) * PAGE_SIZE,
    page * PAGE_SIZE,
  );

  const hasActiveFilters =
    filters.search !== INITIAL_FILTERS.search ||
    filters.type !== INITIAL_FILTERS.type ||
    filters.origin !== INITIAL_FILTERS.origin ||
    filters.dateRange.length > 0 ||
    filters.sortField !== INITIAL_FILTERS.sortField ||
    filters.sortDirection !== INITIAL_FILTERS.sortDirection;

  const isAscending = filters.sortDirection === "ascend";

  return (
    <>
      <Row gutter={8} style={{ marginBottom: 8 }}>
        <Col span={10}>
          <Input.Search
            value={filters.search}
            placeholder={
              tj.searchPlaceholder || "Buscar por NF ou solicitante..."
            }
            onChange={(e) => changeFilters({ search: e.target.value })}
            allowClear
          />
        </Col>
        <Col span={7}>
          <Select
            value={filters.type}
            placeholder={tf.type || "Tipo"}
            onChange={(value) =>
              changeFilters({ type: value, origin: undefined })
            }
            style={{ width: "100%" }}
            allowClear
          >
            <Option value="ENTRY">{getTypeLabel("ENTRY")}</Option>
            <Option value="EXIT">{getTypeLabel("EXIT")}</Option>
          </Select>
        </Col>
        <Col span={7}>
          <Select
            value={filters.origin}
            placeholder={tf.origin || "Origem"}
            onChange={(value) => changeFilters({ origin: value })}
            style={{ width: "100%" }}
            allowClear
          >
            {originOptions.map((origin) => (
              <Option key={origin} value={origin}>
                {getOriginLabel(origin, filters.type)}
              </Option>
            ))}
          </Select>
        </Col>
      </Row>
      <Row gutter={8} style={{ marginBottom: 16 }}>
        <Col span={9}>
          <RangePicker
            value={filters.dateRange}
            format="DD/MM/YYYY"
            placeholder={[
              tf.startDate || "Data inicial",
              tf.endDate || "Data final",
            ]}
            onChange={(dates) => changeFilters({ dateRange: dates || [] })}
            style={{ width: "100%" }}
          />
        </Col>
        <Col span={7}>
          <Select
            value={filters.sortField}
            onChange={(value) => changeFilters({ sortField: value })}
            style={{ width: "100%" }}
            title={ts.label || "Ordenar por"}
          >
            {SORT_OPTIONS.map((option) => (
              <Option key={option.value} value={option.value}>
                {ts[option.labelKey] || option.fallback}
              </Option>
            ))}
          </Select>
        </Col>
        <Col span={2}>
          <Button
            icon={isAscending ? "arrow-up" : "arrow-down"}
            title={
              isAscending
                ? ts.ascending || "Ordem crescente"
                : ts.descending || "Ordem decrescente"
            }
            onClick={() =>
              changeFilters({
                sortDirection: isAscending ? "descend" : "ascend",
              })
            }
            style={{ width: "100%" }}
          />
        </Col>
        <Col span={6}>
          <Button
            disabled={!hasActiveFilters}
            onClick={() => changeFilters(INITIAL_FILTERS)}
            style={{ width: "100%" }}
          >
            <Icon type="close" /> {tf.clear || "Limpar filtros"}
          </Button>
        </Col>
      </Row>

      {hasError && (
        <Alert
          type="error"
          showIcon
          message={tj.loadError || "Erro ao carregar as movimentações do item."}
          style={{ marginBottom: 16 }}
        />
      )}

      <Spin spinning={isLoading}>
        {!isLoading && !hasError && visibleEntries.length === 0 && (
          <Empty description={tj.empty || "Nenhuma movimentação encontrada."} />
        )}
        {visibleEntries.length > 0 && (
          <>
            <div
              style={{
                color: "#888",
                fontSize: 12,
                marginBottom: 8,
                textAlign: "right",
              }}
            >
              {(tj.total || "{count} movimentação(ões)").replace(
                "{count}",
                visibleEntries.length,
              )}
            </div>
            {pagedEntries.map((entry) => (
              <JournalCard
                key={entry.id}
                entry={entry}
                tj={tj}
                typeLabel={getTypeLabel(entry.stockMovementType)}
                originLabel={getOriginLabel(
                  entry.stockMovementOrigin,
                  entry.stockMovementType,
                )}
                onViewInvoice={setInvoiceIdToView}
              />
            ))}
            <Pagination
              size="small"
              current={page}
              pageSize={PAGE_SIZE}
              total={visibleEntries.length}
              onChange={setPage}
              hideOnSinglePage
              style={{ textAlign: "right" }}
            />
          </>
        )}
      </Spin>

      {invoiceIdToView && (
        <DanfeViewerModal
          visible
          invoiceId={invoiceIdToView}
          groupId={groupId}
          farmId={farmId}
          onClose={() => setInvoiceIdToView(null)}
        />
      )}
    </>
  );
};

export default StockItemJournalTab;
